import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import Header from '../components/Header';
import { 
  Trophy, Star, Quote, MapPin, Camera, Share2, Twitter, Facebook,
  Linkedin, Link2, CheckCircle, Loader2, ArrowRight, Heart,
  ThumbsUp, Image, Video, ChevronLeft, ChevronRight, Eye, Sparkles
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';

const API = process.env.REACT_APP_BACKEND_URL;

// Spotlight View Component (Public)
const SpotlightView = ({ spotlight }) => {
  const [copied, setCopied] = useState(false);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  
  const allImages = [
    ...(spotlight.before_images || []).map(img => ({ url: img, type: 'before' })),
    ...(spotlight.after_images || []).map(img => ({ url: img, type: 'after' }))
  ];

  const shareUrl = `${window.location.origin}/spotlights/${spotlight.spotlight_id}`;
  const shareText = `🎉 Check out this amazing transformation! ${spotlight.winner_name} won ${spotlight.giveaway_title} worth $${spotlight.giveaway_value?.toLocaleString()} on Spinser!`;
  
  const shareLinks = {
    twitter: `https://twitter.com/intent/tweet?text=${encodeURIComponent(shareText)}&url=${encodeURIComponent(shareUrl)}`,
    facebook: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}`,
    linkedin: `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(shareUrl)}`,
  };

  const copyLink = async () => {
    await navigator.clipboard.writeText(shareUrl);
    setCopied(true);
    
    // Track share
    fetch(`${API}/api/spotlights/${spotlight.spotlight_id}/share`, { method: 'POST' });
    
    toast.success('Link copied!');
    setTimeout(() => setCopied(false), 3000);
  };

  return (
    <div className="max-w-4xl mx-auto" data-testid="spotlight-view">
      {/* Hero Card */}
      <div className="bg-white rounded-3xl shadow-xl overflow-hidden border border-neutral-100">
        {/* Before/After Gallery */}
        {allImages.length > 0 && (
          <div className="relative">
            <div className="aspect-video bg-neutral-100">
              <img 
                src={allImages[currentImageIndex]?.url} 
                alt={allImages[currentImageIndex]?.type}
                className="w-full h-full object-cover"
              />
              <div className="absolute top-4 left-4">
                <span className={`px-3 py-1 rounded-full text-sm font-bold uppercase ${
                  allImages[currentImageIndex]?.type === 'before' 
                    ? 'bg-neutral-800 text-white' 
                    : 'bg-green-500 text-white'
                }`}>
                  {allImages[currentImageIndex]?.type}
                </span>
              </div>
            </div>
            
            {allImages.length > 1 && (
              <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-2">
                {allImages.map((img, i) => (
                  <button
                    key={`img-dot-${i}-${img?.substring?.(0, 20) || i}`}
                    onClick={() => setCurrentImageIndex(i)}
                    className={`w-3 h-3 rounded-full transition-colors ${
                      i === currentImageIndex ? 'bg-white' : 'bg-white/50'
                    }`}
                  />
                ))}
              </div>
            )}
            
            {allImages.length > 1 && (
              <>
                <button 
                  onClick={() => setCurrentImageIndex(i => i === 0 ? allImages.length - 1 : i - 1)}
                  className="absolute left-4 top-1/2 -translate-y-1/2 w-10 h-10 bg-white/80 rounded-full flex items-center justify-center hover:bg-white"
                >
                  <ChevronLeft className="w-6 h-6" />
                </button>
                <button 
                  onClick={() => setCurrentImageIndex(i => i === allImages.length - 1 ? 0 : i + 1)}
                  className="absolute right-4 top-1/2 -translate-y-1/2 w-10 h-10 bg-white/80 rounded-full flex items-center justify-center hover:bg-white"
                >
                  <ChevronRight className="w-6 h-6" />
                </button>
              </>
            )}
          </div>
        )}

        {/* Content */}
        <div className="p-6 sm:p-8">
          {/* Winner Badge */}
          <div className="flex items-center gap-3 mb-6">
            <div className="w-14 h-14 bg-gradient-to-br from-yellow-400 to-yellow-600 rounded-full flex items-center justify-center">
              <Trophy className="w-7 h-7 text-white" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-neutral-900">{spotlight.winner_name}</h2>
              <p className="text-neutral-500 flex items-center gap-1">
                <MapPin className="w-4 h-4" />
                {spotlight.city}, {spotlight.province_state}
              </p>
            </div>
            {spotlight.status === 'featured' && (
              <span className="ml-auto px-3 py-1 bg-gradient-to-r from-purple-500 to-pink-500 text-white text-xs font-bold rounded-full">
                FEATURED
              </span>
            )}
          </div>

          {/* Prize Info */}
          <div className="bg-gradient-to-r from-green-50 to-emerald-50 rounded-xl p-4 mb-6">
            <p className="text-sm text-green-700 mb-1">Won</p>
            <h3 className="text-lg font-bold text-green-800">{spotlight.giveaway_title}</h3>
            <p className="text-2xl font-bold text-green-600 mt-1">
              ${spotlight.giveaway_value?.toLocaleString()} Value
            </p>
          </div>

          {/* Rating */}
          <div className="flex items-center gap-1 mb-4">
            {[...Array(5)].map((_, i) => (
              <Star 
                key={`star-${i}`} 
                className={`w-5 h-5 ${i < (spotlight.rating || 5) ? 'text-yellow-500 fill-yellow-500' : 'text-neutral-300'}`}
              />
            ))}
            <span className="ml-2 text-sm text-neutral-500">
              {spotlight.rating || 5}/5 Experience
            </span>
          </div>

          {/* Testimonial */}
          {spotlight.testimonial && (
            <div className="relative bg-neutral-50 rounded-xl p-6 mb-6">
              <Quote className="absolute top-4 left-4 w-8 h-8 text-neutral-200" />
              <p className="text-neutral-700 italic pl-8 text-lg leading-relaxed">
                "{spotlight.testimonial}"
              </p>
            </div>
          )}

          {/* Experience Highlights */}
          {spotlight.experience_highlights?.length > 0 && (
            <div className="mb-6">
              <p className="text-sm font-medium text-neutral-600 mb-3">Highlights</p>
              <div className="flex flex-wrap gap-2">
                {spotlight.experience_highlights.map((highlight, i) => (
                  <span key={`highlight-${highlight}-${i}`} className="px-3 py-1 bg-blue-50 text-blue-700 rounded-full text-sm">
                    {highlight}
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* Would Recommend */}
          {spotlight.would_recommend && (
            <div className="flex items-center gap-2 text-green-600 mb-6">
              <ThumbsUp className="w-5 h-5" />
              <span className="font-medium">Would recommend Spinser to others</span>
            </div>
          )}

          {/* Contractor Review */}
          {spotlight.contractor_review && spotlight.contractor_name && (
            <div className="border-t border-neutral-100 pt-6">
              <p className="text-sm text-neutral-500 mb-2">About the contractor</p>
              <p className="font-medium text-neutral-900 mb-1">{spotlight.contractor_name}</p>
              <p className="text-neutral-600 italic">"{spotlight.contractor_review}"</p>
            </div>
          )}

          {/* Stats */}
          <div className="flex items-center gap-4 text-sm text-neutral-400 mt-6 pt-6 border-t border-neutral-100">
            <span className="flex items-center gap-1">
              <Eye className="w-4 h-4" />
              {spotlight.views || 0} views
            </span>
            <span className="flex items-center gap-1">
              <Share2 className="w-4 h-4" />
              {spotlight.shares || 0} shares
            </span>
          </div>

          {/* Share Buttons */}
          <div className="mt-6 pt-6 border-t border-neutral-100">
            <p className="text-sm font-medium text-neutral-600 mb-3">Share this story</p>
            <div className="flex gap-2">
              <a 
                href={shareLinks.twitter}
                target="_blank"
                rel="noopener noreferrer"
                className="flex-1 flex items-center justify-center gap-2 p-3 bg-[#1DA1F2] text-white rounded-lg font-medium hover:bg-[#1a8cd8] transition-colors"
              >
                <Twitter className="w-4 h-4" />
                Twitter
              </a>
              <a 
                href={shareLinks.facebook}
                target="_blank"
                rel="noopener noreferrer"
                className="flex-1 flex items-center justify-center gap-2 p-3 bg-[#1877F2] text-white rounded-lg font-medium hover:bg-[#166fe5] transition-colors"
              >
                <Facebook className="w-4 h-4" />
                Facebook
              </a>
              <button 
                onClick={copyLink}
                className={`flex-1 flex items-center justify-center gap-2 p-3 rounded-lg font-medium transition-colors ${
                  copied ? 'bg-green-100 text-green-700' : 'bg-neutral-100 text-neutral-700 hover:bg-neutral-200'
                }`}
              >
                {copied ? <CheckCircle className="w-4 h-4" /> : <Link2 className="w-4 h-4" />}
                {copied ? 'Copied!' : 'Copy'}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* CTA */}
      <div className="mt-8 text-center">
        <Link to="/giveaways">
          <Button size="lg" className="bg-[#FF3B30] hover:bg-[#DC2626]">
            <Sparkles className="w-5 h-5 mr-2" />
            Enter FREE Giveaways
            <ArrowRight className="w-5 h-5 ml-2" />
          </Button>
        </Link>
      </div>
    </div>
  );
};

// Spotlight Creation Form Component
const CreateSpotlightForm = ({ giveawayId, onSuccess }) => {
  const { token } = useAuth();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    testimonial: '',
    rating: 5,
    before_images: [],
    after_images: [],
    contractor_review: '',
    would_recommend: true,
    experience_highlights: []
  });

  const highlights = [
    'Quick turnaround', 'Professional team', 'Great communication',
    'Quality work', 'Clean workspace', 'On budget', 'Exceeded expectations'
  ];

  const toggleHighlight = (h) => {
    setFormData(prev => ({
      ...prev,
      experience_highlights: prev.experience_highlights.includes(h)
        ? prev.experience_highlights.filter(x => x !== h)
        : [...prev.experience_highlights, h]
    }));
  };

  const handleSubmit = async () => {
    if (!formData.testimonial.trim()) {
      toast.error('Please share your experience');
      return;
    }

    try {
      setLoading(true);
      const response = await fetch(`${API}/api/giveaways/${giveawayId}/spotlight`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(formData)
      });

      if (!response.ok) throw new Error('Failed to create spotlight');
      
      const data = await response.json();
      toast.success('Spotlight created! It will be reviewed shortly.');
      onSuccess?.(data.spotlight);
    } catch (error) {
      toast.error(error.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="create-spotlight-form">
      <div>
        <Label>Share Your Experience *</Label>
        <Textarea
          value={formData.testimonial}
          onChange={(e) => setFormData({...formData, testimonial: e.target.value})}
          placeholder="Tell us about your experience winning this giveaway and the service you received..."
          rows={4}
          className="mt-2"
          data-testid="testimonial-input"
        />
      </div>

      <div>
        <Label>Rate Your Experience</Label>
        <div className="flex items-center gap-1 mt-2">
          {[1, 2, 3, 4, 5].map((star) => (
            <button
              key={star}
              onClick={() => setFormData({...formData, rating: star})}
              className="p-1"
            >
              <Star 
                className={`w-8 h-8 transition-colors ${
                  star <= formData.rating 
                    ? 'text-yellow-500 fill-yellow-500' 
                    : 'text-neutral-300 hover:text-yellow-300'
                }`}
              />
            </button>
          ))}
        </div>
      </div>

      <div>
        <Label>Experience Highlights</Label>
        <div className="flex flex-wrap gap-2 mt-2">
          {highlights.map((h) => (
            <button
              key={h}
              onClick={() => toggleHighlight(h)}
              className={`px-3 py-1.5 rounded-full text-sm transition-colors ${
                formData.experience_highlights.includes(h)
                  ? 'bg-[#FF3B30] text-white'
                  : 'bg-neutral-100 text-neutral-600 hover:bg-neutral-200'
              }`}
            >
              {h}
            </button>
          ))}
        </div>
      </div>

      <div>
        <Label>Before Photo URL</Label>
        <Input
          value={formData.before_images[0] || ''}
          onChange={(e) => setFormData({...formData, before_images: e.target.value ? [e.target.value] : []})}
          placeholder="https://..."
          className="mt-2"
        />
      </div>

      <div>
        <Label>After Photo URL</Label>
        <Input
          value={formData.after_images[0] || ''}
          onChange={(e) => setFormData({...formData, after_images: e.target.value ? [e.target.value] : []})}
          placeholder="https://..."
          className="mt-2"
        />
      </div>

      <div>
        <Label>Review the Contractor (Optional)</Label>
        <Textarea
          value={formData.contractor_review}
          onChange={(e) => setFormData({...formData, contractor_review: e.target.value})}
          placeholder="How was your experience with the contractor?"
          rows={2}
          className="mt-2"
        />
      </div>

      <div className="flex items-center gap-2">
        <input
          type="checkbox"
          id="recommend"
          checked={formData.would_recommend}
          onChange={(e) => setFormData({...formData, would_recommend: e.target.checked})}
          className="w-4 h-4"
        />
        <Label htmlFor="recommend" className="cursor-pointer">
          I would recommend Spinser to others
        </Label>
      </div>

      <Button 
        onClick={handleSubmit}
        disabled={loading}
        className="w-full bg-[#FF3B30] hover:bg-[#DC2626]"
        data-testid="submit-spotlight-btn"
      >
        {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Sparkles className="w-4 h-4 mr-2" />}
        Create Spotlight
      </Button>
    </div>
  );
};

// Main Component
const WinnerSpotlight = () => {
  const { spotlightId, giveawayId } = useParams();
  const { token, user } = useAuth();
  const navigate = useNavigate();
  const [spotlight, setSpotlight] = useState(null);
  const [loading, setLoading] = useState(true);
  const [giveaway, setGiveaway] = useState(null);
  const [showCreateForm, setShowCreateForm] = useState(false);

  const fetchSpotlight = useCallback(async () => {
    try {
      // Try to get spotlight by ID from URL
      const response = await fetch(`${API}/api/spotlights?status=all&limit=100`);
      if (response.ok) {
        const data = await response.json();
        const found = data.spotlights?.find(s => s.spotlight_id === spotlightId);
        if (found) {
          setSpotlight(found);
        }
      }
    } catch (error) {
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  }, [spotlightId]);

  const fetchGiveawayAndSpotlight = useCallback(async () => {
    try {
      // Fetch giveaway details
      const giveawayRes = await fetch(`${API}/api/giveaways/${giveawayId}`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      });
      
      if (giveawayRes.ok) {
        const giveawayData = await giveawayRes.json();
        setGiveaway(giveawayData);
        
        // Check if spotlight exists
        try {
          const spotlightRes = await fetch(`${API}/api/giveaways/${giveawayId}/spotlight`);
          if (spotlightRes.ok) {
            const spotlightData = await spotlightRes.json();
            setSpotlight(spotlightData);
          }
        } catch (e) {
          // Spotlight may not exist yet - this is expected for new giveaways
          console.debug('No spotlight found for giveaway:', giveawayId);
        }
      }
    } catch (error) {
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  }, [giveawayId, token]);

  useEffect(() => {
    if (spotlightId) {
      fetchSpotlight();
    } else if (giveawayId) {
      fetchGiveawayAndSpotlight();
    }
  }, [spotlightId, giveawayId, fetchSpotlight, fetchGiveawayAndSpotlight]);

  if (loading) {
    return (
      <div className="min-h-screen bg-neutral-50">
        <Header />
        <div className="pt-32 flex items-center justify-center">
          <Loader2 className="w-8 h-8 animate-spin text-[#FF3B30]" />
        </div>
      </div>
    );
  }

  // Show existing spotlight
  if (spotlight) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-yellow-50 via-white to-neutral-50">
        <Header />
        <div className="pt-32 pb-16 px-4">
          <SpotlightView spotlight={spotlight} />
        </div>
      </div>
    );
  }

  // Show create form for winners
  const isWinner = giveaway?.winner_id === user?.user_id;
  const isAdmin = user?.role === 'admin';

  if (giveaway && (isWinner || isAdmin)) {
    return (
      <div className="min-h-screen bg-neutral-50">
        <Header />
        <div className="pt-32 pb-16 px-4">
          <div className="max-w-2xl mx-auto">
            <div className="text-center mb-8">
              <div className="w-16 h-16 mx-auto bg-gradient-to-br from-yellow-400 to-yellow-600 rounded-full flex items-center justify-center mb-4">
                <Trophy className="w-8 h-8 text-white" />
              </div>
              <h1 className="text-2xl font-bold text-neutral-900">Create Your Winner Spotlight</h1>
              <p className="text-neutral-500 mt-2">
                Share your winning story and inspire others!
              </p>
            </div>

            <div className="bg-white rounded-2xl border border-neutral-200 p-6">
              <div className="bg-green-50 rounded-xl p-4 mb-6">
                <p className="text-sm text-green-700">You won:</p>
                <h3 className="font-bold text-green-800">{giveaway.title}</h3>
                <p className="text-green-600">${giveaway.value?.toLocaleString()} Value</p>
              </div>

              <CreateSpotlightForm 
                giveawayId={giveawayId} 
                onSuccess={(s) => setSpotlight(s)}
              />
            </div>
          </div>
        </div>
      </div>
    );
  }

  // No spotlight found
  return (
    <div className="min-h-screen bg-neutral-50">
      <Header />
      <div className="pt-32 pb-16 px-4 text-center">
        <Trophy className="w-16 h-16 mx-auto text-neutral-300 mb-4" />
        <h1 className="text-2xl font-bold text-neutral-900 mb-2">Spotlight Not Found</h1>
        <p className="text-neutral-500 mb-6">This winner spotlight doesn't exist yet.</p>
        <Link to="/giveaways">
          <Button className="bg-[#FF3B30] hover:bg-[#DC2626]">
            Browse Giveaways
          </Button>
        </Link>
      </div>
    </div>
  );
};

export default WinnerSpotlight;
